/**
 * OrbitModule — the client face of Orbits (P6): transient proximity groups that
 * persist across world hops.
 *
 * The panel (toggle 'O') lets a player:
 *   • Form (or grow) an Orbit from the avatars within 10 m (`formOrbit`).
 *   • See their current Orbit's roster, its validated state, and its preferred
 *     world (the modal world of its members).
 *   • Confirm a real interaction (`markOrbitInteraction`) — the gate that flips
 *     the Orbit `validated`. Until a member does this the Orbit accrues NO
 *     downstream value, and an Orbit nobody validates simply ages out after 4 h.
 *   • Follow the Orbit to its preferred world when it has drifted elsewhere —
 *     the "Join Orbit?" hop, which is how an Orbit travels together.
 *
 * Everything here mirrors authoritative `orbit` / `orbit_member` rows; the server
 * owns every clustering, validation, and expiry decision. Orbits are free and
 * transient — they are NOT guilds (that is P9) and grant no reputation (P7).
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';

function shortHex(hex: string): string {
  return hex.length > 12 ? `${hex.slice(0, 6)}\u2026${hex.slice(-4)}` : hex;
}

export class OrbitModule implements IModule {
  readonly name = 'Orbit';

  private panel!: HTMLElement;
  private formBtn!: HTMLButtonElement;
  private rosterEl!: HTMLElement;
  private hopEl!: HTMLElement;
  private noteEl!: HTMLElement;
  private open = false;

  init(_app: App): void {
    this.panel = document.getElementById('orbit') as HTMLElement;
    this.buildPanel();

    bus.on('atlas:orbit-updated', () => this.render());
    bus.on('atlas:world-entered', () => this.render());
    bus.on('atlas:account-ready', () => this.render());

    window.addEventListener('keydown', (e) => {
      const typing =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement instanceof HTMLSelectElement;
      if (e.key === 'Escape' && this.open) {
        this.close();
      } else if ((e.key === 'o' || e.key === 'O') && !typing && atlas.hasAccount) {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  private buildPanel(): void {
    this.panel.innerHTML = '';

    const title = document.createElement('h2');
    title.textContent = 'Orbits';
    this.panel.appendChild(title);

    const blurb = document.createElement('p');
    blurb.className = 'orbit-blurb';
    blurb.textContent =
      'Form a transient group with the people around you. An Orbit travels with you across worlds and lasts as long as you keep meeting.';
    this.panel.appendChild(blurb);

    this.formBtn = document.createElement('button');
    this.formBtn.className = 'orbit-btn';
    this.formBtn.textContent = 'Form Orbit with people nearby';
    this.formBtn.addEventListener('click', () => this.form());
    this.panel.appendChild(this.formBtn);

    this.rosterEl = document.createElement('div');
    this.rosterEl.className = 'orbit-roster';
    this.panel.appendChild(this.rosterEl);

    this.hopEl = document.createElement('div');
    this.hopEl.className = 'orbit-hop';
    this.panel.appendChild(this.hopEl);

    this.noteEl = document.createElement('div');
    this.noteEl.className = 'orbit-note';
    this.panel.appendChild(this.noteEl);
  }

  private toggle(): void {
    this.open ? this.close() : this.show();
  }

  private show(): void {
    this.open = true;
    this.panel.classList.add('show');
    this.render();
  }

  private close(): void {
    this.open = false;
    this.panel.classList.remove('show');
  }

  /** The Orbit the local player belongs to (id string), or null. */
  private myOrbitId(): string | null {
    const me = atlas.identityHex;
    if (me === null) return null;
    for (const m of atlas.orbitMembers.values()) {
      if (m.identity === me) return m.orbitId.toString();
    }
    return null;
  }

  /** A friendly name for a world the Orbit prefers. */
  private worldLabel(worldId: bigint): string {
    if (atlas.commonsWorldId !== null && worldId === atlas.commonsWorldId) return 'the Commons';
    if (atlas.personalWorldId !== null && worldId === atlas.personalWorldId) return 'your world';
    return `world #${worldId}`;
  }

  private render(): void {
    if (!this.open) return;
    const orbitId = this.myOrbitId();

    if (orbitId === null) {
      this.rosterEl.innerHTML =
        '<div class="orbit-empty">You are not in an Orbit yet. Stand near other players and Form one.</div>';
      this.hopEl.style.display = 'none';
      return;
    }

    const orbit = atlas.orbits.get(orbitId);
    const members = [...atlas.orbitMembers.values()]
      .filter((m) => m.orbitId.toString() === orbitId)
      .sort((a, b) => (a.joinedAt < b.joinedAt ? -1 : a.joinedAt > b.joinedAt ? 1 : 0));

    const me = atlas.identityHex;
    const status = orbit?.validated
      ? '<span class="orbit-badge ok">validated</span>'
      : '<span class="orbit-badge pending">unvalidated</span>';

    const rows = members
      .map((m) => {
        const you = m.identity === me ? ' <span class="you">(you)</span>' : '';
        return `<div class="orbit-member">${shortHex(m.identity)}${you}</div>`;
      })
      .join('');

    this.rosterEl.innerHTML =
      `<div class="orbit-head">Your Orbit ${status}` +
      `<span class="orbit-count">${members.length} member${members.length === 1 ? '' : 's'}</span></div>` +
      rows +
      `<button class="orbit-btn confirm" data-confirm="1">Confirm we are interacting</button>`;

    const confirmBtn = this.rosterEl.querySelector<HTMLButtonElement>('[data-confirm]');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => this.markInteraction(orbitId));
    }

    // "Join Orbit?" hop: the Orbit prefers a world we are not in.
    const pref = orbit?.preferredWorldId ?? null;
    if (pref !== null && atlas.currentWorldId !== null && pref !== atlas.currentWorldId) {
      this.hopEl.innerHTML = `Your Orbit is gathering in <b>${this.worldLabel(pref)}</b>. `;
      const btn = document.createElement('button');
      btn.className = 'orbit-btn follow';
      btn.textContent = 'Follow your Orbit';
      btn.addEventListener('click', () => {
        bus.emit('atlas:enter-world', { worldId: pref });
        this.setNote(`Following your Orbit to ${this.worldLabel(pref)}…`);
      });
      this.hopEl.appendChild(btn);
      this.hopEl.style.display = 'block';
    } else {
      this.hopEl.style.display = 'none';
    }
  }

  private form(): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.setNote('Forming Orbit…');
    void conn.reducers
      .formOrbit({})
      .then(() => this.setNote('Orbit formed with everyone nearby.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private markInteraction(orbitId: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.setNote('Confirming interaction…');
    void conn.reducers
      .markOrbitInteraction({ orbitId: BigInt(orbitId) })
      .then(() => this.setNote('Orbit validated — it now counts.', 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private setNote(text: string, cls: '' | 'ok' | 'err' = ''): void {
    this.noteEl.textContent = text;
    this.noteEl.className = `orbit-note ${cls}`;
  }
}
